/**
 * 身分證件模組 - 輔助函數與常數
 * @module identity/helpers
 */

import type { IdentityDocumentStatus, IdentityDocumentSummary, IdentityDocument } from './types';

/**
 * 證件狀態顯示配置
 */
export const IDENTITY_STATUS_CONFIG: Record<
  IdentityDocumentStatus,
  { label: string; color: string; bgColor: string; icon: string }
> = {
  not_uploaded: {
    label: '未上傳',
    color: 'text-gray-600',
    bgColor: 'bg-gray-100',
    icon: 'ri-file-upload-line',
  },
  pending: {
    label: '待驗證',
    color: 'text-amber-600',
    bgColor: 'bg-amber-100',
    icon: 'ri-time-line',
  },
  verified: {
    label: '已驗證',
    color: 'text-emerald-600',
    bgColor: 'bg-emerald-100',
    icon: 'ri-checkbox-circle-fill',
  },
  rejected: {
    label: '已拒絕',
    color: 'text-red-600',
    bgColor: 'bg-red-100',
    icon: 'ri-close-circle-line',
  },
  masked: {
    label: '已遮蔽',
    color: 'text-slate-600',
    bgColor: 'bg-slate-100',
    icon: 'ri-eye-off-line',
  },
  destroyed: {
    label: '已銷毀',
    color: 'text-stone-600',
    bgColor: 'bg-stone-100',
    icon: 'ri-delete-bin-line',
  },
};

/**
 * 判斷整體證件狀態（取最嚴格的）
 * 優先順序：destroyed > rejected > masked > not_uploaded > pending > verified
 */
export function computeOverallStatus(
  frontStatus: IdentityDocumentStatus,
  backStatus: IdentityDocumentStatus
): IdentityDocumentStatus {
  const severity: Record<IdentityDocumentStatus, number> = {
    destroyed: 6,
    rejected: 5,
    masked: 4,
    not_uploaded: 3,
    pending: 2,
    verified: 1,
  };
  return severity[frontStatus] >= severity[backStatus] ? frontStatus : backStatus;
}

/**
 * 從證件列表計算摘要
 */
export function summarizeIdentityDocuments(
  documents: IdentityDocument[]
): IdentityDocumentSummary {
  const front = documents
    .filter((d) => d.document_type === 'front')
    .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())[0];

  const back = documents
    .filter((d) => d.document_type === 'back')
    .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())[0];

  const frontStatus = front?.status ?? 'not_uploaded';
  const backStatus = back?.status ?? 'not_uploaded';

  const lastUploaded = documents.length > 0
    ? documents.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())[0].created_at
    : null;

  const lastVerified = documents.filter((d) => d.verified_at).sort(
    (a, b) => new Date(b.verified_at!).getTime() - new Date(a.verified_at!).getTime()
  )[0]?.verified_at ?? null;

  const overallStatus = computeOverallStatus(frontStatus, backStatus);

  return {
    frontStatus,
    backStatus,
    overallStatus,
    lastUploadedAt: lastUploaded,
    lastVerifiedAt: lastVerified,
    identityReady: overallStatus === 'verified',
    frontUploaded: frontStatus !== 'not_uploaded',
    backUploaded: backStatus !== 'not_uploaded',
    source: 'mock',
  };
}

/**
 * 檢查是否已驗證：優先使用 gateway 的 identityReady，fallback 到 overallStatus === verified
 */
export function isIdentityVerified(summary: IdentityDocumentSummary): boolean {
  return summary.identityReady === true || summary.overallStatus === 'verified';
}

/**
 * 檢查是否可以核驗（staff 不可核驗，只有 owner / super_admin / store_manager 可核驗）
 */
export function canVerifyIdentity(role: string | null | undefined): boolean {
  return role === 'owner' || role === 'super_admin' || role === 'store_manager';
}

/**
 * 檢查是否可以協助上傳（任何後台人員都可協助）
 */
export function canAssistUpload(role: string | null | undefined): boolean {
  return role === 'owner' || role === 'super_admin' || role === 'store_manager' || role === 'staff' || role === 'part_time';
}

/**
 * Edge Function 名稱常數
 */
export const IDENTITY_EDGE_FUNCTIONS = {
  /** 上傳證件 */
  upload: 'customer-identity-document-upload',
  /** 讀取證件（取得 signed URL） */
  read: 'customer-identity-document-read',
  /** 審核證件 */
  verify: 'customer-identity-document-verify',
  /** 訂單合約證件資產 */
  orderContractAssets: 'order-contract-identity-assets',
  /** 合約作廢遮蔽 */
  orderContractVoidRedact: 'order-contract-void-redact',
} as const;

/**
 * 證件類型顯示名稱
 */
export function getDocumentTypeLabel(type: 'front' | 'back'): string {
  return type === 'front' ? '身分證正面' : '身分證反面';
}

/**
 * 檢查是否顯示「請先完成身分證件」提示
 */
export function shouldShowIdentityWarning(summary: IdentityDocumentSummary): boolean {
  return summary.overallStatus !== 'verified';
}

/**
 * 安全取得證件狀態顯示配置（帶 fallback，避免 undefined crash）
 */
export function getIdentityStatusConfig(
  status: IdentityDocumentStatus | undefined | string | null
) {
  const safeStatus = (status ?? 'not_uploaded') as IdentityDocumentStatus;
  return IDENTITY_STATUS_CONFIG[safeStatus] ?? IDENTITY_STATUS_CONFIG.not_uploaded;
}